/**
 * Error log trace detail modal — per docs/specs/error-logs.md §4.2.
 *
 * Expands a single error row into trace + deep-link to Cloudflare observability.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { Copy, ExternalLink } from 'lucide-react';
import { Chip } from '../components/chip';
import { Modal } from '../components/modal';
import { useToast } from '../components/toast';
import type { ErrorLogRow } from '../../lib/types';
import {
    severityTone,
    traceAvailabilityLabel,
    traceCopyLabel,
    traceUrlForRow
} from './infra/error-logs';

export function ErrorLogDetailModal({
    row,
    onClose
}: {
    row: ErrorLogRow | null;
    onClose: () => void;
}) {
    const { toast } = useToast();

    if (!row) return null;

    const traceId = (row.trace_id || '').trim();
    const cfLink = traceUrlForRow(row);
    const hasTraceId = !!cfLink;

    const handleCopyTraceId = async () => {
        if (!traceId) {
            toast('No correlation id available for this row', { kind: 'danger' });
            return;
        }

        try {
            await navigator.clipboard.writeText(traceId);
            toast(row.trace_state === 'real' ? 'Copied request id' : 'Copied correlation id', {
                kind: 'info',
                detail: traceId
            });
        } catch {
            toast('Unable to access clipboard', { kind: 'danger' });
        }
    };

    return (
        <Modal
            open={!!row}
            onClose={onClose}
            title={row.code}
            subtitle={row.service}
            width={640}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleCopyTraceId}>
                        <Copy size={13} /> {traceCopyLabel(row)}
                    </button>
                    {hasTraceId ? (
                        <a
                            className="btn btn-primary"
                            href={cfLink}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <ExternalLink size={13} /> Open in Cloudflare
                        </a>
                    ) : (
                        <button className="btn btn-ghost" type="button" disabled>
                            <ExternalLink size={13} /> Cloudflare trace unavailable
                        </button>
                    )}
                </>
            }
        >
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <Chip tone={severityTone(row.severity)}>{row.severity}</Chip>
                <Chip tone="muted">{row.ts}</Chip>
                {row.user_email && <Chip tone="info">{row.user_email}</Chip>}
                <Chip tone="muted">×{row.count} occurrences</Chip>
                <Chip tone={hasTraceId ? 'success' : 'muted'}>{traceAvailabilityLabel(row)}</Chip>
            </div>

            <div className="form-field">
                <span className="form-label">Message</span>
                <pre
                    style={{
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: 10,
                        fontSize: 12,
                        fontFamily: 'JetBrains Mono, monospace',
                        color: 'var(--text-primary)',
                        margin: 0,
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {row.message}
                </pre>
            </div>

            <div className="form-field">
                <span className="form-label">Trace id</span>
                <code
                    style={{
                        display: 'block',
                        background: 'var(--bg-input)',
                        padding: '8px 10px',
                        fontSize: 11
                    }}
                >
                    {traceId || '—'}
                </code>
                <div className="form-hint">
                    {hasTraceId
                        ? 'Cloudflare clusters related errors — follow the link to see full trace + grouping.'
                        : 'This row is backed by a persisted admin correlation id, not a direct Cloudflare ray id.'}
                </div>
            </div>
        </Modal>
    );
}
